import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api';
import { local } from '../lib/storage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Download, Trash2, Plus, Package, Upload, Pencil } from 'lucide-react';
import { GST_RATES } from '../lib/states';
import { formatINR } from '../lib/gst';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

function parseCSV(text) {
  const rows = [];
  let row = [], cell = '', quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i], next = text[i + 1];
    if (ch === '"' && quoted && next === '"') { cell += '"'; i += 1; }
    else if (ch === '"') quoted = !quoted;
    else if (ch === ',' && !quoted) { row.push(cell.trim()); cell = ''; }
    else if ((ch === '\n' || ch === '\r') && !quoted) {
      if (cell || row.length) { row.push(cell.trim()); rows.push(row); row = []; cell = ''; }
      if (ch === '\r' && next === '\n') i += 1;
    } else cell += ch;
  }
  if (cell || row.length) { row.push(cell.trim()); rows.push(row); }
  return rows.filter((r) => r.some(Boolean));
}

function itemsFromCSV(text) {
  const rows = parseCSV(text);
  if (!rows.length) return [];
  const headers = rows[0].map((h) => h.toLowerCase().trim());
  const hasHeaders = headers.includes('description') || headers.includes('item') || headers.includes('service');
  const body = hasHeaders ? rows.slice(1) : rows;
  const idx = (names, fallback) => {
    const found = names.map((n) => headers.indexOf(n)).find((i) => i >= 0);
    return found == null ? fallback : found;
  };
  return body.map((r) => ({
    description: r[idx(['description', 'item', 'service', 'name'], 0)] || '',
    hsn: r[idx(['hsn', 'sac', 'hsn/sac'], 1)] || '',
    unit: r[idx(['unit'], 2)] || 'Nos',
    purchasePrice: parseFloat(r[idx(['purchaseprice', 'purchase price', 'cost', 'cost price'], 3)] || '0') || 0,
    rate: parseFloat(r[idx(['saleprice', 'sale price', 'rate', 'price', 'amount'], 4)] || '0') || 0,
    gstRate: parseFloat(r[idx(['gstrate', 'gst rate', 'gst'], 5)] || '18') || 18,
  })).filter((i) => i.description);
}

function downloadCSV(filename, rows) {
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function Items() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ description: '', hsn: '', unit: 'Nos', purchasePrice: 0, rate: 0, gstRate: 18 });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    (async () => {
      if (user) {
        try { const { data } = await apiClient.get('/items'); setItems(data); } catch { setItems([]); }
      } else {
        setItems(local.getItems());
      }
    })();
  }, [user]);

  const resetForm = () => {
    setEditingId(null);
    setForm({ description: '', hsn: '', unit: 'Nos', purchasePrice: 0, rate: 0, gstRate: 18 });
  };

  const add = async () => {
    if (!form.description) { toast.error(`${t('description')} ${t('required')}`); return; }
    if (user) {
      try {
        const { data } = editingId ? await apiClient.put(`/items/${editingId}`, form) : await apiClient.post('/items', form);
        setItems((list) => editingId ? list.map((item) => (item.id === editingId ? data : item)) : [data, ...list]);
        toast.success(t('saved'));
      } catch { toast.error(t('failedToSave')); }
    } else {
      const next = editingId
        ? [{ ...form, id: editingId }, ...local.getItems().filter((item) => item.id !== editingId)].slice(0, 10)
        : local.addItem({ ...form, id: crypto.randomUUID() });
      if (editingId) local.setItems(next);
      setItems(next);
      toast.success(t('savedLocally'));
    }
    resetForm();
  };

  const edit = (item) => {
    setEditingId(item.id);
    setForm({
      description: item.description || '',
      hsn: item.hsn || '',
      unit: item.unit || 'Nos',
      purchasePrice: Number(item.purchasePrice || 0),
      rate: Number(item.rate || 0),
      gstRate: Number(item.gstRate || 18),
    });
  };

  const uploadCSV = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const imported = itemsFromCSV(await file.text());
    if (!imported.length) { toast.error(t('noItemsFoundCsv')); return; }
    if (user) {
      const saved = [];
      for (const item of imported) {
        try {
          const { data } = await apiClient.post('/items', item);
          saved.push(data);
        } catch {}
      }
      setItems((list) => [...saved, ...list]);
      toast.success(`${t('imported')} ${saved.length} ${t('items')}`);
    } else {
      let next = local.getItems();
      imported.forEach((item) => { next = local.addItem({ ...item, id: crypto.randomUUID() }); });
      setItems(next);
      toast.success(`${t('imported')} ${imported.length} ${t('items')} ${t('locally')}`);
    }
  };

  const downloadTemplate = () => {
    downloadCSV('billjaldi-items-template.csv', [
      ['description', 'hsn', 'unit', 'purchasePrice', 'salePrice', 'gstRate'],
      ['Website design service', '998314', 'Nos', '8000', '12000', '18'],
    ]);
  };

  const remove = async (i) => {
    if (user) { try { await apiClient.delete(`/items/${i.id}`); } catch {} }
    else { local.removeItem(i.id); }
    setItems((list) => list.filter((x) => x.id !== i.id));
  };

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="items-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">{t('itemsServices')}</h1>
        <p className="text-sm text-slate-500 mt-1">{user ? t('itemsSource') : t('lastTenBrowser')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h3 className="font-heading font-bold">{editingId ? t('editItem') : t('addItem')}</h3>
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={downloadTemplate} className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 border border-slate-200 rounded-md px-2 py-1 hover:bg-slate-50" data-testid="items-csv-template">
                <Download className="w-3.5 h-3.5" /> {t('template')}
              </button>
              <label className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 border border-slate-200 rounded-md px-2 py-1 cursor-pointer hover:bg-slate-50">
                <Upload className="w-3.5 h-3.5" /> {t('csv')}
                <input type="file" accept=".csv,text/csv" className="hidden" onChange={uploadCSV} data-testid="items-csv-upload" />
              </label>
            </div>
          </div>
          <div className="space-y-3">
            <div><Label>{t('description')}</Label><Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="item-form-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>HSN/SAC</Label><Input value={form.hsn} onChange={(e) => setForm((f) => ({ ...f, hsn: e.target.value }))} className="font-mono" data-testid="item-form-hsn" /></div>
              <div><Label>{t('unit')}</Label><Input value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} data-testid="item-form-unit" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t('purchasePrice')} (₹)</Label><Input type="number" step="0.01" value={form.purchasePrice} onChange={(e) => setForm((f) => ({ ...f, purchasePrice: parseFloat(e.target.value) || 0 }))} data-testid="item-form-purchase-price" /></div>
              <div><Label>{t('salePrice')} (₹)</Label><Input type="number" step="0.01" value={form.rate} onChange={(e) => setForm((f) => ({ ...f, rate: parseFloat(e.target.value) || 0 }))} data-testid="item-form-rate" /></div>
            </div>
            <div>
              <Label>GST %</Label>
              <Select value={String(form.gstRate)} onValueChange={(v) => setForm((f) => ({ ...f, gstRate: parseFloat(v) }))}>
                <SelectTrigger data-testid="item-form-gst"><SelectValue /></SelectTrigger>
                <SelectContent>{GST_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={add} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white" data-testid="item-form-submit"><Plus className="w-4 h-4 mr-1" /> {editingId ? t('update') : t('add')}</Button>
              {editingId && <Button variant="outline" onClick={resetForm} data-testid="item-form-cancel">{t('cancel')}</Button>}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          {items.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-xl" data-testid="items-empty">
              <Package className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <div className="text-slate-400">{t('noItemsYet')}</div>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <table className="w-full text-sm" data-testid="items-list">
                <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 text-left">
                  <tr><th className="px-4 py-3 font-bold">{t('description')}</th><th className="px-4 py-3 font-bold">HSN/SAC</th><th className="px-4 py-3 font-bold text-right">{t('purchase')}</th><th className="px-4 py-3 font-bold text-right">{t('sale')}</th><th className="px-4 py-3 font-bold text-right">{t('gst')}</th><th className="px-4 py-3" /></tr>
                </thead>
                <tbody>
                  {items.map((i) => (
                    <tr key={i.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="px-4 py-3 font-semibold">{i.description}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">{i.hsn || '—'}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">{formatINR(i.purchasePrice || 0)}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">{formatINR(i.rate)}</td>
                      <td className="px-4 py-3 text-right">{i.gstRate}%</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => edit(i)} data-testid={`item-edit-${i.id}`}><Pencil className="w-4 h-4 text-slate-400 hover:text-slate-700" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => remove(i)} data-testid={`item-remove-${i.id}`}><Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
